/*
Item: Application Login Form
Name: Login
Format: Page, full screen
*/

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Sidebar } from 'primereact/sidebar';
import { Password } from 'primereact/password';
import { Messages } from 'primereact/messages';
import { Panel } from 'primereact/panel';
import { Growl } from 'primereact/growl';
import MessageService from '../service/MessageService';
import '../styles/login.css';
import { configVersion } from '../utils/configUtils';

const { ENVIRONMENT } = window;
const VERSION = configVersion[ENVIRONMENT];

// renders a list of admins in the 'Kontaktinformationen' div
function renderUserlist(entryList) {
  if (entryList) {
    return (
      <tbody>
        {entryList.map((s) => (
          <tr key={s.name}>
            <td>{s.contact.lastName}</td>
            <td>{s.contact.phone}</td>
            <td>
              <a href={`mailto:${s.contact.email}`}>{s.contact.email}</a>
            </td>
          </tr>
        ))}
      </tbody>
    );
  }
  return <tbody />;
}

const ErrorType = Object.freeze({
  wrongPw: 0,
  loggedIn: 5104,
  needNewPw: 699, // neues PW erforderlich
  needOtherNewPw: 688, // neues PW muss sich von alten PW unterscheiden
  invalidPw: 687,
  wrongPwRepetition: 690,
});

// Regex für die verbotenen Zeichen
const forbiddenCharsRegex = /[\\/:*"<>|]/;
const maxPwLength = 20;

/** class which provides the component for login of new and existing user.
 * Includes the error-handling and a full-page div with 'kontaktinformationen'
 * which can be shown on click* */
export default class Login extends Component {
  constructor() {
    super();
    this.showError = this.showError.bind(this);
    this.wrapMessage = this.wrapMessage.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.loadAdminList = this.loadAdminList.bind(this);
    this.state = {
      visibleFullScreen: false,
      username: '',
      password: '',
      newPassword: '',
      newPasswordAgain: '',
      errorNumber: ErrorType.wrongPw,
      positiveAnswer: null,
      isNewPasswordValid: true,
      isNewPasswordRepetitionValid: true,
    };
    this.messageService = new MessageService();
  }

  componentDidMount = async () => {
    this.setState({
      visibleFullScreen: false, // for slow loading
    });
    const userData = await this.messageService.getUserContact();
    if (userData) {
      if (userData[1] === 'error') {
        this.growl.show({ severity: userData[1], summary: 'Übersicht der Fachadministratoren', detail: userData[0] });
      } else {
        this.setState({
          userData: userData.entrys,
        });
      }
    }
    const { info } = this.props;
    this.setState({
      visibleFullScreen: info || false,
    });
  };

  handleChange = (e) => {
    this.messages.clear();
    this.setState({
      [e.target.name]: e.target.value,
    });
  };

    handleNewPasswordChange = (e) => {
      this.messages.clear();
      // Überprüfung, ob eines der verbotenen Zeichen existiert
      const isNewPasswordValid = this.validatePassword(e.target.value);
      this.setState({
        [e.target.name]: e.target.value, isNewPasswordValid,
      });
    };

    handleNewPasswordRepetitionChange = (e) => {
      this.messages.clear();
      // Überprüfung, ob eines der verbotenen Zeichen existiert
      const isNewPasswordRepetitionValid = this.validatePassword(e.target.value);
      this.setState({
        [e.target.name]: e.target.value, isNewPasswordRepetitionValid,
      });
    };

    handleSubmit(event) {
      event.preventDefault();
      if (this.validateForm()) {
        this.wrapMessage();
      }
    }

    validatePassword = (password) => {
      return !forbiddenCharsRegex.test(password) && password.length <= maxPwLength;
    }

    /* handleKeyDown(event) {
    if (event.keyCode === 13) {
      // this.wrapMessage();
    }
  } */

  wrapMessage = async () => {
    const {
      errorNumber, username, password, newPassword, newPasswordAgain, positiveAnswer,
    } = this.state;
    if (errorNumber === ErrorType.loggedIn) {
      const res = await this.messageService.postSessionRenewal(
        positiveAnswer,
      );
      this.showError(res);
    } else {
      const escapedPassword = password.replace(/\$/g, '$$$$'); // due to usage of replace
      // const escapedNewPassword = newPassword.replace(/\$/g, '$');
      // const escapedNewPasswordAgain = newPasswordAgain.replace(/\$/g, '$');
      const res = await this.messageService.postUserLogin(
        username,
        escapedPassword,
        newPassword,
        newPasswordAgain,
      );
      const textMessage = res[0];
      this.showError(textMessage);

      const nextState = {
        errorNumber: res[1],
        positiveAnswer: res[2],
      };

      // Die errorNumber 10688 gleicht in diesem Fall dem ErrorType.needOtherNewPw
      if (nextState.errorNumber === 10688) nextState.errorNumber = ErrorType.needOtherNewPw;

      if (textMessage) {
        if (nextState.errorNumber === ErrorType.wrongPw) {
          nextState.username = '';
          nextState.password = '';
        }
      }
      this.setState(nextState);
    }
  };

  validateForm() {
    const { errorNumber } = this.state;
    const { username } = this.state;
    const { password } = this.state;
    const { newPassword, isNewPasswordValid } = this.state;
    const { newPasswordAgain, isNewPasswordRepetitionValid } = this.state;
    const validate = username.length > 0 && password.length > 0;
    const valiateToo = (errorNumber === ErrorType.needNewPw
      || errorNumber === ErrorType.needOtherNewPw
      || errorNumber === ErrorType.invalidPw
      || errorNumber === ErrorType.wrongPwRepetition)
      ? newPassword.length > 0 && newPasswordAgain.length > 0
        && isNewPasswordValid && isNewPasswordRepetitionValid
      : true;
    return validate && valiateToo;
  }

  showError(messageDetail) {
    const { errorNumber } = this.state;
    const msg = {
      sticky: true,
      severity:
        errorNumber === ErrorType.needNewPw
          || ErrorType === ErrorType.needOtherNewPw
          || errorNumber === ErrorType.loggedIn
          ? 'info'
          : 'error',
      detail: messageDetail,
    };
    // console.log(msg);
    if (messageDetail) {
      this.messages.show(msg);
    }
  }

  edit(adminData) {
    // console.log(adminData);
    const { history } = this.props;
    history.push({
      pathname: '/edituser',
      state: {
        entry: adminData,
      },
    });
  }

  loadAdminList() {
    /*
    let userData =  this.messageService.getUserContact();
    this.setState({
      userData: userData.entrys
    });
    */
    this.setState({ visibleFullScreen: true });
  }

  showNewPassword() {
    const { errorNumber, isNewPasswordValid } = this.state;
    const { newPassword } = this.state;
    if (errorNumber === ErrorType.needNewPw
      || errorNumber === ErrorType.needOtherNewPw
      || errorNumber === ErrorType.invalidPw
      || errorNumber === ErrorType.wrongPwRepetition
    ) {
      return (
        <div className="p-col-12">
          <span className="md-inputfield">
            <Password
              name="newPassword"
              id="newPassword"
              value={newPassword}
              autoFocus
              onChange={(e) => this.handleNewPasswordChange(e)}
              /* onKeyDown={(e) => this.handleKeyDown(e)} */
              tooltip="Geben Sie hier Ihr neues Kennwort ein."
              tooltipOptions={{ event: 'focus' }}
              feedback={false}
            />
            <label htmlFor="newPassword">Neues Kennwort*</label>
            {!isNewPasswordValid && (
              <small id="password-error" className="p-invalid" style={{ color: '#f44336', display: 'block', marginTop: '5px' }}>
                Das Kennwort darf maximal 20 Zeichen lang sein und folgende Zeichen nicht enthalten: \ / : * " &lt; &gt; |
              </small>
            )}
          </span>
        </div>
      );
    }
    return (<></>);
  }

  showNewPasswordAgain() {
    const { errorNumber, isNewPasswordRepetitionValid } = this.state;
    const { newPasswordAgain } = this.state;
    if (errorNumber === ErrorType.needNewPw
      || errorNumber === ErrorType.needOtherNewPw
      || errorNumber === ErrorType.invalidPw
      || errorNumber === ErrorType.wrongPwRepetition
    ) {
      return (
        <div className="p-col-12">
          <span className="md-inputfield">
            <Password
              name="newPasswordAgain"
              id="newPasswordAgain"
              value={newPasswordAgain}
              onChange={(e) => this.handleNewPasswordRepetitionChange(e)}
              /* onKeyDown={(e) => this.handleKeyDown(e)} */
              tooltip="Geben Sie hier Ihr neues Kennwort nochmals ein."
              tooltipOptions={{ event: 'focus' }}
              feedback={false}
            />
            <label htmlFor="newPasswordAgain">Neues Kennwort wiederholen*</label>
            {!isNewPasswordRepetitionValid && (
              <small id="password-error" className="p-invalid" style={{ color: '#f44336', display: 'block', marginTop: '5px' }}>
                Das Kennwort darf maximal 20 Zeichen lang sein und folgende Zeichen nicht enthalten: \ / : * " &lt; &gt; |
              </small>
            )}
          </span>
        </div>
      );
    }
    return (<></>);
  }

  renderSubmitButton() {
    const { errorNumber } = this.state;
    return (
      <div className="p-col-6">
        <Button
          type="submit"
          id={errorNumber === ErrorType.loggedIn ? 'yes' : 'submit'}
          className={
            this.validateForm()
              ? 'p-button-success'
              : 'p-button-primary'
          }
          label={
            errorNumber === ErrorType.loggedIn ? 'Ja' : 'Anmelden'
          }
          icon="pi-md-check"
          disabled={!this.validateForm()}
          tooltip={
            this.validateForm()
              ? 'Klick oder Enter'
              : 'Bitte füllen Sie alle mit * gekennzeichneten Pflichtfelder aus'
          }
          tooltipOptions={{ position: 'bottom' }}
        />
      </div>
    );
  }

  renderKontaktinformationen() {
    const panelHeader = (
      <span>
        Kontaktieren Sie bitte den für Sie zuständigen Fachadministrator, wenn
        Sie sich als neuer Nutzer registrieren lassen möchten oder als bereits
        registrierter Nutzer Probleme bei der Anmeldung haben.
        {' '}
        <br />
        Der Fachadministrator kann Ihnen ein neues Kennwort zuweisen oder Ihren
        Zugang freigeben, falls dieser gesperrt wurde.
      </span>
    );
    const { visibleFullScreen, userData } = this.state;
    return (
      <Sidebar
        style={{ overflow: 'scroll' }}
        visible={visibleFullScreen}
        fullScreen
        baseZIndex={1000000}
        onHide={() => this.setState({ visibleFullScreen: false })}
        closeOnEscape
      >
        <h1>Übersicht der Fachadministratoren</h1>
        <div className="p-grid dashboard">
          <div className="p-col-12">
            <div className="card">
              <div className="p-col-12 p-lg-12 p-grid-nopad">
                <div className="p-col-12 global-sales">
                  <Panel header={panelHeader}>
                    <table>
                      <thead>
                        <tr>
                          <th>Name Institution</th>
                          <th>Tel.</th>
                          <th>E-Mail</th>
                        </tr>
                      </thead>
                      {renderUserlist(userData)}
                    </table>
                  </Panel>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button
          type="button"
          id="back"
          onClick={() => this.setState({ visibleFullScreen: false })}
          label="Zurück"
          icon="pi-md-arrow-back"
          className="p-button-success"
          style={{ marginRight: '.25em' }}
        />
      </Sidebar>
    );
  }

  renderPasswordField() {
    const { errorNumber } = this.state;
    const { password } = this.state;
    return (
      <div className="p-col-12">
        <span className="md-inputfield">
          <Password
            name="password"
            id="password"
            value={password}
            disabled={
              errorNumber === ErrorType.needNewPw
              || errorNumber === ErrorType.needOtherNewPw
              || errorNumber === ErrorType.invalidPw
              || errorNumber === ErrorType.wrongPwRepetition
              || errorNumber === ErrorType.loggedIn
            }
            onChange={(e) => this.handleChange(e)}
            /* onKeyDown={(e) => this.handleKeyDown(e)} */
            tooltip="Geben Sie hier Ihr Kennwort ein."
            tooltipOptions={{ event: 'focus' }}
            feedback={false}
          />
          <label htmlFor="password">Kennwort*</label>
        </span>
      </div>
    );
  }

  renderUsernameField() {
    const { username } = this.state;
    const { errorNumber } = this.state;
    return (
      <div className="p-col-12">
        <span className="md-inputfield">
          <InputText
            name="username"
            id="username"
            value={username}
            disabled={
              errorNumber === ErrorType.needNewPw
              || errorNumber === ErrorType.needOtherNewPw
              || errorNumber === ErrorType.invalidPw
              || errorNumber === ErrorType.wrongPwRepetition
              || errorNumber === ErrorType.loggedIn
            }
            onChange={(e) => this.handleChange(e)}
            /* onKeyDown={(e) => this.handleKeyDown(e)} */
            autoFocus
            tooltip="Geben Sie hier Ihren Benutzernamen ein."
            tooltipOptions={{ event: 'focus' }}
          />
          <label htmlFor="username">Benutzername*</label>
        </span>
      </div>
    );
  }

  renderShowKontaktinformationen() {
    return (
      <div className="p-col-12">
        <div
          style={{
            textAlign: 'left',
            fontSize: '12px',
            color: '#607D8B',
            marginBottom: '5px',
          }}
        >
          Haben Sie Interesse an einer Neuregistrierung oder
          Probleme bei der Anmeldung? Kontaktieren Sie bitte den
          für Sie zuständigen Fachadministrator:
        </div>
        <Button
          type="button"
          id="register-forgot"
          className="p-button-secondary"
          label="Kontaktinformationen Fachadministrator"
          onClick={() => this.loadAdminList()}
        />
      </div>
    );
  }

  render() {
    const { errorNumber } = this.state;
    return (
      <>
        {this.renderKontaktinformationen()}
        <form ref={this.form} onSubmit={this.handleSubmit}>
          <Growl style={{ position: 'absolute', left: '70.5%', top: '100px' }} ref={(el) => { this.growl = el; }} />
          <div className="login-body">
            <div
              className="login-panel p-fluid"
              style={{ overflow: 'auto', height: '600px' }}
            >
              <div style={{
                fontSize: '9px', color: 'white', background: '#bdbdbd', textAlign: 'right',
              }}
              >
                {VERSION}
              </div>
              <div className="login-panel-header">
                <h2>Anmeldung</h2>
              </div>
              <div className="login-panel-content" style={{ padding: '20px 80px' }}>
                <div className="p-grid">
                  <div className="p-col-12">
                    <Messages
                      ref={(el) => { this.messages = el; }}
                      style={{ marginBottom: '1em' }}
                    />
                  </div>
                  <div className="p-col-12">
                    <div className="p-grid">
                      {/* <div className="p-col-6" style={{ display: 'none' }}>
                        <Button
                          type="button"
                          id="no"
                          className="p-button-primary"
                          label="Ja"
                          icon="pi-md-check"
                          onClick={() => window.location.reload()}
                        />
                      </div>
                      <div className="p-col-6" style={{ display: 'none' }}>
                        <Button
                          type="button"
                          id="no"
                          className="p-button-danger"
                          label="Nein"
                          icon="pi-md-cancel"
                          onClick={() => window.location.reload()}
                        />
                      </div> */}
                      {this.renderShowKontaktinformationen()}
                      {this.renderUsernameField()}
                      {this.renderPasswordField()}
                      {this.showNewPassword()}
                      {this.showNewPasswordAgain()}
                      {this.renderSubmitButton()}
                      {/* Cancel Button at this time not needed */}
                      <div className="p-col-6">
                        <Button
                          type="button"
                          id={errorNumber === ErrorType.loggedIn ? 'no' : 'cancel'}
                          className="p-button-primary"
                          label={
                            errorNumber === ErrorType.loggedIn ? 'Nein' : 'Abbrechen'
                          }
                          icon="pi-md-cancel"
                          onClick={() => window.location.reload()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </>
    );
  }
}

Login.propTypes = {
  info: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object),
};

Login.defaultProps = {
  history: null,
};
